import React, { useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const OtpBoxInput = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
  autoFocus = false,
}) => {
  const inputsRef = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

  useEffect(() => {
    if (autoFocus && inputsRef.current[0] && !disabled) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus, disabled]);

  const focusIndex = (index) => {
    const el = inputsRef.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const updateDigits = useCallback(
    (nextDigits) => {
      onChange(nextDigits.join('').slice(0, 6));
    },
    [onChange]
  );

  const handleChange = (index, raw) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    updateDigits(next);
    if (digit && index < 5) focusIndex(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
        updateDigits(next);
      } else if (index > 0) {
        next[index - 1] = '';
        updateDigits(next);
        focusIndex(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusIndex(index - 1);
    } else if (e.key === 'ArrowRight' && index < 5) {
      focusIndex(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = Array.from({ length: 6 }, (_, i) => pasted[i] || '');
    updateDigits(next);
    const focusTo = Math.min(pasted.length, 5);
    focusIndex(focusTo);
  };

  return (
    <Row>
      {digits.map((digit, index) => (
        <Box
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          disabled={disabled}
          $hasError={hasError}
          aria-label={`OTP digit ${index + 1}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </Row>
  );
};

const Row = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const Box = styled.input`
  width: 44px;
  height: 52px;
  text-align: center;
  font-size: 22px;
  font-weight: 600;
  border: 1px solid ${(p) => (p.$hasError ? '#dc2626' : '#e2e8f0')};
  border-radius: 8px;
  outline: none;
  background: #fff;
  color: #1e293b;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$hasError ? '#dc2626' : '#2563eb')};
  }

  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
    color: #94a3b8;
  }
`;

export default OtpBoxInput;
